import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import { toggleSavedDesign } from "../lib/ownerPortal";
import { openNewVersion } from "../lib/architectPortalB";
import { toast } from "sonner";
import SignInPromptModal from "../components/SignInPromptModal";
import DesignCard from "../components/DesignCard";
import { WatchHintChip } from "../components/WatchHintChip";
import {
  formatInt,
  formatPriceFull,
  formatPriceCompact,
  formatTypology,
  assetUrl,
} from "../lib/format";
import {
  Heart,
  MessageSquare,
  Layers,
  ChevronRight,
  ArrowUpRight,
  Zap,
  Pencil,
} from "lucide-react";
import BackBar from "../components/BackBar";
import WatermarkedImage from "../components/WatermarkedImage";

/* ───────── Reveal-on-scroll helper (local to this page) ───────── */

function useInView({ threshold = 0.15, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, as: As = "div", style, ...rest }) {
  const [ref, inView] = useInView();
  return (
    <As
      ref={ref}
      className={`wn-reveal${inView ? " is-visible" : ""}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </As>
  );
}

/* ───────── Page ───────── */

export default function DesignDetail() {
  const { designId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const variantIdx =
    params.get("variant") != null ? Number(params.get("variant")) : null;

  const { role, user, refreshUser } = useRole();
  const [design, setDesign] = useState(null);
  const [related, setRelated] = useState(null);
  const [err, setErr] = useState(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInAction, setSignInAction] = useState("");
  const [savingDesign, setSavingDesign] = useState(false);
  const isOwner = role === "owner_builder" && user?.id;
  // Architect viewing their own design — surfaces the version/edit toolbar.
  const isOwningArchitect =
    role === "architect" && user?.id && design?.architect_id === user.id;
  const isSaved =
    isOwner && (user?.saved_design_ids || []).includes(designId);

  useEffect(() => {
    setDesign(null);
    setErr(null);
    setRelated(null);
    api
      .get(`/marketplace/designs/${designId}`)
      .then((r) => setDesign(r.data))
      .catch((e) => setErr(e));
    api
      .get(`/marketplace/designs/${designId}/related`)
      .then((r) => setRelated(r.data))
      .catch(() => setRelated({ designs: [], architect_firm: null }));
  }, [designId]);

  const gated = (label) => () => {
    setSignInAction(label);
    setSignInOpen(true);
  };

  const handleSave = async () => {
    if (!isOwner) return gated("Save this design")();
    setSavingDesign(true);
    try {
      await toggleSavedDesign(user.id, designId);
      await refreshUser();
    } finally {
      setSavingDesign(false);
    }
  };

  const handleConsult = () => {
    if (!isOwner) return gated("Request a consultation")();
    navigate(`/owner/consultations/new?design=${encodeURIComponent(designId)}`);
  };

  const [openingVersion, setOpeningVersion] = useState(false);
  const handleNewVersion = async () => {
    if (!user?.id || openingVersion) return;
    setOpeningVersion(true);
    try {
      const next = await openNewVersion(designId, user.id);
      toast.success(`Opened version ${next.version || 2} as a Draft.`);
      navigate(`/architect/designs/new?draft=${next.id}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not open new version.",
      );
    } finally {
      setOpeningVersion(false);
    }
  };

  const setVariant = (idx) => {
    const next = new URLSearchParams(params);
    if (idx == null) next.delete("variant");
    else next.set("variant", String(idx));
    setParams(next, { replace: true });
  };

  if (err) {
    return (
      <div data-testid="design-detail-error" style={{ color: "#A23B2E" }}>
        Could not load this design.{" "}
        <Link to="/marketplace" style={{ color: "#1B1F4B" }}>
          Back to Marketplace
        </Link>
      </div>
    );
  }
  if (!design) {
    return <div style={{ color: "#6C6B85" }}>Loading design…</div>;
  }

  const hard = design.adjusted_cost_2026 || 0;
  const plans = design.plan_purchase_price || 0;
  const soft = design.soft_costs || 0;
  const iterations = design.iterations || [];
  const activeVariant = variantIdx != null ? iterations[variantIdx] : null;
  const variantDelta = activeVariant?.cost_delta || 0;
  const allIn = hard + plans + soft + variantDelta;

  const lotLabel =
    design.min_lot_width >= 50
      ? "50 ft +"
      : design.min_lot_width >= 40
      ? "40 ft Standard"
      : "30 ft Narrow";

  return (
    <section
      data-testid="design-detail-page"
      className="animate-fade-in-up"
    >
      <BackBar
        parent={{ to: "/marketplace", label: "Marketplace" }}
        trail={[
          { label: "Marketplace", to: "/marketplace" },
          { label: design.name },
        ]}
      />

      {/* ─── § 01 · Editorial hero header (full-width) ─── */}
      <Reveal>
        <header style={{ paddingBottom: 28 }}>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 28,
                height: 2,
                background: "#B14E2F",
              }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              {formatTypology(design.typology)} · {lotLabel}
              {design.ten_day_eligible && (
                <>
                  {" · "}
                  <span
                    className="inline-flex items-center"
                    style={{
                      gap: 4,
                      color: "#4F6149",
                    }}
                  >
                    <Zap size={11} strokeWidth={2.5} />
                    10-Day Path
                  </span>
                </>
              )}
            </span>
          </div>

          <h1
            className="mt-5"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(40px, 5.4vw, 62px)",
              fontWeight: 500,
              lineHeight: 1.04,
              letterSpacing: "-0.01em",
              color: "#1B1F4B",
              margin: 0,
              maxWidth: "min(1000px, 80vw)",
            }}
            data-testid="design-name"
          >
            {design.name}
          </h1>

          <p
            className="mt-4"
            data-testid="design-architect-byline"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 19,
              lineHeight: 1.55,
              maxWidth: 720,
              margin: 0,
            }}
          >
            A {formatInt(design.sqft)}-square-foot{" "}
            {(formatTypology(design.typology) || "").toLowerCase()} in{" "}
            <span style={{ color: "#272636" }}>
              {design.primary_neighborhood || "Indianapolis"}
            </span>
            . Designed by{" "}
            <Link
              to={`/architects/${design.architect_id}`}
              data-testid="architect-link"
              style={{
                color: "#1B1F4B",
                fontStyle: "normal",
                fontWeight: 600,
                textDecoration: "underline",
                textUnderlineOffset: 4,
                textDecorationColor: "#D9D8E4",
                textDecorationThickness: 1,
              }}
            >
              {design.architect_firm || "—"}
            </Link>
            .
          </p>

          {isOwningArchitect && (
            <ArchitectOwnerToolbar
              design={design}
              onNewVersion={handleNewVersion}
              busy={openingVersion}
            />
          )}
        </header>
      </Reveal>

      {/* ─── Action band (full-width, ABOVE image) ─── */}
      <Reveal delay={40}>
        <ActionBand
          tags={design.lifestyle_tags || []}
          onConsult={handleConsult}
          onSave={handleSave}
          isSaved={isSaved}
          saving={savingDesign}
          visualizerHref={`/marketplace/${designId}/visualizer`}
        />
      </Reveal>

      {/* ─── Image + unified fact-sheet card ─── */}
      <div
        className="grid lg:grid-cols-[1.45fr_1fr] gap-12 items-stretch"
        style={{ paddingTop: 32 }}
      >
        <Reveal>
          <div
            data-testid="exterior-photo"
            style={{
              width: "100%",
              height: "100%",
              minHeight: 480,
              aspectRatio: "4 / 3",
              backgroundColor: "#EEEDF3",
              backgroundImage: `url(${assetUrl(design.exterior_photo)})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: 4,
              border: "1px solid #D9D8E4",
            }}
          />
        </Reveal>

        <Reveal delay={140}>
          <FactSheet
            design={design}
            hard={hard}
            plans={plans}
            soft={soft}
            activeVariant={activeVariant}
            variantDelta={variantDelta}
            allIn={allIn}
          />
        </Reveal>

        {/* "How to buy a pre-reviewed plan" — sidebar cross-reference
            to the Owners track of the video field guide. Sits right
            beneath the cost block where the visitor is deciding
            whether to proceed; gives them a low-commitment "watch
            the walkthrough first" entry point before committing to
            an inquiry. */}
        <Reveal delay={180}>
          <WatchHintChip
            slug="buy-pre-reviewed-plan"
            variant="card"
            testid="design-detail-watch-hint"
          />
        </Reveal>
      </div>

      {/* ─── § 02 · Floor plan ─── */}
      <SectionDivider />
      <Reveal>
        <SectionHeading
          marker="§ 02"
          eyebrow="Floor plan"
          title="Indicative layout."
        />
      </Reveal>
      <Reveal delay={80}>
        <p
          className="mt-4 max-w-[760px]"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 16,
            lineHeight: 1.6,
          }}
        >
          A schematic of room placement and circulation — paired with a
          typology sketch showing the exterior form. Not for construction.
          Wall thicknesses, door swings, fixtures, and structural details are
          confirmed with the architect during consultation.
        </p>
      </Reveal>
      <div
        className="mt-8 grid md:grid-cols-2 gap-8 items-start"
        data-testid="floor-plan-pair"
      >
        <Reveal>
          <figure style={{ margin: 0 }}>
            <WatermarkedImage
              testid="floor-plan-frame"
              imgTestid="floor-plan"
              src={assetUrl(design.floor_plan_image)}
              alt={`${design.name} floor plan`}
              watermark={design.architect_firm || design.architect_name || "WHOLE Neighborhoods"}
              style={{
                backgroundColor: "#FBFAFD",
                border: "1px solid #D9D8E4",
                borderRadius: 4,
              }}
            />
            <figcaption
              className="wn-label mt-3"
              style={{ color: "#8F3C23" }}
            >
              Plan · room placement
            </figcaption>
          </figure>
        </Reveal>
        <Reveal delay={120}>
          <figure style={{ margin: 0 }}>
            <WatermarkedImage
              testid="typology-sketch-frame"
              imgTestid="typology-sketch"
              src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/sketches/${
                ["single_family", "duplex", "adu"].includes(design.typology)
                  ? design.typology
                  : "single_family"
              }.jpg`}
              alt={`${(design.typology || "").replace(/_/g, " ")} typology sketch`}
              watermark={design.architect_firm || design.architect_name || "WHOLE Neighborhoods"}
              style={{
                aspectRatio: "620 / 540",
                backgroundColor: "#FBFAFD",
                border: "1px solid #D9D8E4",
                borderRadius: 4,
              }}
              imgStyle={{
                aspectRatio: "620 / 540",
                objectFit: "cover",
              }}
            />
            <figcaption
              className="wn-label mt-3"
              style={{ color: "#8F3C23" }}
            >
              Sketch · exterior form
            </figcaption>
          </figure>
        </Reveal>
      </div>

      {/* ─── § 03 · Variants ─── */}
      {iterations.length > 0 && (
        <>
          <SectionDivider />
          <Reveal>
            <SectionHeading
              marker="§ 03"
              eyebrow="Variants"
              title="Same plan, refined options."
              trailing={
                variantIdx != null && (
                  <button
                    className="wn-btn wn-btn-tertiary"
                    style={{ height: "auto", padding: 0, fontSize: 13 }}
                    onClick={() => setVariant(null)}
                    data-testid="variant-clear"
                  >
                    Clear variant
                  </button>
                )
              }
            />
          </Reveal>
          <Reveal delay={100}>
            <div
              className="mt-8"
              style={{
                borderTop: "1px solid #D9D8E4",
                borderBottom: "1px solid #D9D8E4",
              }}
              data-testid="variants-section"
            >
              {iterations.map((it, i) => {
                const active = variantIdx === i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setVariant(active ? null : i)}
                    data-testid={`variant-${i}`}
                    className="w-full text-left"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) auto auto",
                      alignItems: "center",
                      columnGap: 24,
                      padding: "20px 4px",
                      borderTop:
                        i === 0 ? "none" : "1px solid #D9D8E4",
                      background: "transparent",
                      cursor: "pointer",
                      transition: "background-color 160ms ease-out",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        "rgba(177,78,47,0.03)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <div>
                      <div
                        className="wn-label"
                        style={{
                          color: active ? "#8F3C23" : "#6C6B85",
                        }}
                      >
                        {active ? "Selected variant" : `Variant ${String(i + 1).padStart(2, "0")}`}
                      </div>
                      <div
                        className="mt-1"
                        style={{
                          fontFamily: '"Fraunces", Georgia, serif',
                          fontWeight: 600,
                          fontSize: 19,
                          color: "#1B1F4B",
                          lineHeight: 1.2,
                        }}
                      >
                        {it.label}
                      </div>
                    </div>
                    <div
                      className="tabular-nums"
                      style={{
                        fontWeight: 700,
                        fontSize: 17,
                        color: it.cost_delta >= 0 ? "#8F3C23" : "#4F6149",
                      }}
                    >
                      {it.cost_delta >= 0 ? "+" : "−"}
                      {formatPriceCompact(Math.abs(it.cost_delta))}
                    </div>
                    <span
                      aria-hidden
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 999,
                        border: `2px solid ${active ? "#B14E2F" : "#9AA0C0"}`,
                        backgroundColor: active ? "#B14E2F" : "transparent",
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </Reveal>
        </>
      )}

      {/* ─── § 04 · Previously built ─── */}
      {(design.prior_builds || []).length > 0 && (
        <>
          <SectionDivider />
          <Reveal>
            <SectionHeading
              marker="§ 04"
              eyebrow={`Previously built · ${design.prior_builds.length} ${
                design.prior_builds.length === 1 ? "completed home" : "completed homes"
              }`}
              title="Real cost, real timeline, real contractors."
              dek={
                "Close-out actuals from owners who already built from this "
                + "plan. The Estimated all-in above is re-anchored to "
                + "these outcomes — and the contractors below have already "
                + "pulled permits for this design, so they're worth a call."
              }
            />
          </Reveal>
          <Reveal delay={120}>
            <PriorBuildsBlock builds={design.prior_builds} />
          </Reveal>
        </>
      )}

      {/* ─── § 05 · What happens next ─── */}
      <SectionDivider />
      <Reveal>
        <SectionHeading
          marker="§ 05"
          eyebrow="What happens next"
          title="From request to scheduled consultation."
        />
      </Reveal>
      <Reveal delay={100}>
        <ol
          className="grid md:grid-cols-3"
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            marginTop: 56,
            columnGap: 48,
            rowGap: 28,
          }}
          data-testid="next-steps"
        >
          {NEXT_STEPS.map((s) => (
            <li key={s.n} data-testid={`next-step-${s.n}`}>
              <div
                className="tabular-nums"
                style={{
                  fontWeight: 700,
                  fontSize: 36,
                  lineHeight: 1,
                  color: "#1B1F4B",
                  letterSpacing: "-0.02em",
                }}
              >
                {s.n}
              </div>
              <span
                aria-hidden
                style={{
                  display: "block",
                  width: 32,
                  height: 2,
                  background: "#B14E2F",
                  marginTop: 12,
                  marginBottom: 12,
                }}
              />
              <h3
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 19,
                  color: "#1B1F4B",
                  lineHeight: 1.2,
                  margin: 0,
                }}
              >
                {s.title}
              </h3>
              <p
                className="mt-2"
                style={{
                  color: "#272636",
                  fontSize: 14,
                  lineHeight: 1.55,
                }}
              >
                {s.body}
              </p>
            </li>
          ))}
        </ol>
      </Reveal>

      {/* ─── § 06 · More from architect ─── */}
      {related && related.designs.length > 0 && (
        <>
          <SectionDivider />
          <Reveal>
            <SectionHeading
              marker="§ 06"
              eyebrow={`From ${related.architect_firm || "this architect"}`}
              title={`More from ${related.architect_firm || "this architect"}.`}
            />
          </Reveal>
          <Reveal delay={100}>
            <RelatedGrid
              designs={related.designs}
              firm={related.architect_firm || "this architect"}
              architectId={related.architect_id || design.architect_id}
            />
          </Reveal>
        </>
      )}

      <SignInPromptModal
        open={signInOpen}
        onOpenChange={setSignInOpen}
        action={signInAction}
      />
    </section>
  );
}

/* ─────────────────────── Subcomponents ─────────────────────── */

const NEXT_STEPS = [
  {
    n: "01",
    title: "Submit a request",
    body:
      "Share your parcel, timeline, and budget. The architect receives a structured brief — no inbox spam.",
  },
  {
    n: "02",
    title: "Architect reviews",
    body:
      "Typical response within 2 business days. The architect confirms fit and surfaces any code or site considerations.",
  },
  {
    n: "03",
    title: "Consultation scheduled",
    body:
      "A 45-minute paid session ($275) locks in fit, scope, and next steps. Refundable if the architect cannot proceed.",
  },
];

function SectionDivider() {
  return (
    <div
      aria-hidden
      style={{
        borderTop: "1px solid #D9D8E4",
        marginTop: 56,
        marginBottom: 36,
      }}
    />
  );
}

function SectionHeading({ marker, eyebrow, title, trailing }) {
  return (
    <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-end">
      <div>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 28,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            {marker} · {eyebrow}
          </span>
        </div>
        {title && (
          <h2
            className="mt-4"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: "clamp(28px, 3.4vw, 38px)",
              lineHeight: 1.15,
              color: "#1B1F4B",
              letterSpacing: "-0.005em",
              margin: 0,
              paddingBottom: 4,
            }}
          >
            {title}
          </h2>
        )}
      </div>
      {trailing && <div>{trailing}</div>}
    </div>
  );
}

/* ─────────── Unified fact-sheet card (right column) ─────────── */

function FactSheet({
  design,
  hard,
  plans,
  soft,
  activeVariant,
  variantDelta,
  allIn,
}) {
  // Variance-adjust the 2026 Estimated all-in by the MEDIAN variance
  // observed across the design's prior_builds. This keeps the headline
  // estimate as the cost-formula number, but tells the buyer what the
  // middle of the actually-built cohort spent (in current dollars,
  // because we apply the variance % to today's allIn, not to the
  // historical dollars on each prior build). Shown only when ≥2 builds
  // exist — a single data point is not a band.
  const priorBuilds = design.prior_builds || [];
  const variances = priorBuilds
    .map((b) => Number(b.variance_vs_estimate_pct))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);
  let medianVariance = null;
  let adjustedAllIn = null;
  let bandLow = null;
  let bandHigh = null;
  if (variances.length >= 2) {
    const mid = Math.floor(variances.length / 2);
    medianVariance =
      variances.length % 2 === 0
        ? (variances[mid - 1] + variances[mid]) / 2
        : variances[mid];
    adjustedAllIn = Math.round(allIn * (1 + medianVariance / 100));
    bandLow = Math.round(allIn * (1 + variances[0] / 100));
    bandHigh = Math.round(
      allIn * (1 + variances[variances.length - 1] / 100)
    );
  }
  return (
    <div
      data-testid="fact-sheet"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* §  Specs */}
      <div style={{ padding: "22px 24px 22px" }}>
        <SectionLabel>Specifications</SectionLabel>
        <div
          className="grid grid-cols-2 mt-3"
          style={{ columnGap: 24, rowGap: 16 }}
        >
          <Spec label="Bedrooms" value={design.beds} numeric />
          <Spec label="Bathrooms" value={design.baths} numeric />
          <Spec label="Square feet" value={formatInt(design.sqft)} numeric />
          <Spec label="Lot width" value={`${design.min_lot_width} ft`} numeric />
          <Spec
            label="Compatible zoning"
            value={(design.zoning_fits || []).join(", ")}
          />
          <Spec
            label="Style"
            value={(design.style || "").replace(/_/g, " ")}
          />
        </div>
      </div>

      {/* §  Cost */}
      <div
        style={{
          padding: "20px 24px 22px",
          borderTop: "1px solid #D9D8E4",
        }}
      >
        <SectionLabel>Estimated all-in cost · 2026</SectionLabel>
        <div className="mt-3">
          <CostRow label="Hard construction cost" value={formatPriceFull(hard)} />
          <CostRow label="Plans ($2,500 + $1/sq ft)" value={formatPriceFull(plans)} />
          <CostRow label="Permits & inspections" value={formatPriceFull(soft)} />
          {activeVariant && (
            <CostRow
              label={`Variant · ${activeVariant.label}`}
              value={`${variantDelta >= 0 ? "+" : "−"}${formatPriceFull(
                Math.abs(variantDelta),
              )}`}
            />
          )}
        </div>
        <div
          style={{
            borderTop: "1px solid #D9D8E4",
            marginTop: 12,
            paddingTop: 14,
          }}
          className="flex items-baseline justify-between"
        >
          <span className="wn-label" style={{ color: "#1B1F4B" }}>
            Estimated all-in
          </span>
          <span
            data-testid="total-all-in"
            className="tabular-nums"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 800,
              fontSize: 30,
              color: "#8F3C23",
              lineHeight: 1,
              letterSpacing: "-0.01em",
            }}
          >
            {formatPriceFull(allIn)}
          </span>
        </div>
        {adjustedAllIn != null && (
          <div
            data-testid="cost-actuals-adjusted-block"
            className="mt-4"
            style={{
              borderTop: "1px solid #C9D2BD",
              paddingTop: 12,
            }}
          >
            <div
              className="flex items-baseline justify-between"
              style={{ gap: 12 }}
            >
              <span
                className="wn-label"
                style={{ color: "#4F6149", letterSpacing: "0.08em" }}
              >
                Adjusted by {priorBuilds.length} actual builds
              </span>
              <span
                data-testid="cost-actuals-adjusted-value"
                className="tabular-nums"
                style={{
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 800,
                  fontSize: 26,
                  color: "#3A4A35",
                  lineHeight: 1,
                  letterSpacing: "-0.01em",
                }}
              >
                {formatPriceFull(adjustedAllIn)}
              </span>
            </div>
            <div
              className="mt-2 flex items-baseline justify-between"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 12,
                lineHeight: 1.5,
                gap: 12,
              }}
            >
              <span data-testid="cost-actuals-variance-label">
                {medianVariance === 0
                  ? "median actuals matched the estimate exactly"
                  : `median variance ${medianVariance > 0 ? "+" : ""}${medianVariance.toFixed(
                      1,
                    )}% vs estimate`}
              </span>
              <span
                className="tabular-nums"
                data-testid="cost-actuals-band"
                style={{ whiteSpace: "nowrap" }}
              >
                range {formatPriceFull(bandLow)} – {formatPriceFull(bandHigh)}
              </span>
            </div>
          </div>
        )}
        {priorBuilds.length === 1 && (
          <div
            data-testid="cost-single-build-note"
            className="mt-3"
            style={{
              padding: "8px 10px",
              border: "1px solid #DDD9CE",
              borderLeft: "3px solid #4F6149",
              borderRadius: 4,
              background: "#F4F2EC",
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              fontSize: 11.5,
              color: "#4F6149",
              lineHeight: 1.5,
            }}
          >
            One actual build of this plan came in{" "}
            <span style={{ fontWeight: 700, fontStyle: "normal" }}>
              {Number(priorBuilds[0].variance_vs_estimate_pct || 0) >= 0
                ? "+"
                : ""}
              {Number(priorBuilds[0].variance_vs_estimate_pct || 0).toFixed(1)}%
            </span>{" "}
            vs the estimate. One data point is not a band — see &ldquo;Previously
            built&rdquo; above for the close-out numbers.
          </div>
        )}
      </div>

      {/* §  Timeline — flex:1 so the card matches image height */}
      <div
        style={{
          padding: "20px 24px 22px",
          borderTop: "1px solid #D9D8E4",
          flex: 1,
        }}
      >
        <SectionLabel>Delivery timeline</SectionLabel>
        <ul
          className="mt-3"
          style={{ listStyle: "none", padding: 0, margin: 0 }}
        >
          <TimelineRow
            label="Plans delivered"
            value="~6 weeks after consultation"
            first
          />
          <TimelineRow
            label="Permit window"
            value={
              design.ten_day_eligible
                ? "10-day path eligible"
                : "30-60 days · standard review"
            }
          />
          <TimelineRow
            label="Construction"
            value="8-10 months · typical"
          />
        </ul>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      className="wn-label"
      style={{ color: "#8F3C23" }}
    >
      {children}
    </div>
  );
}

function TimelineRow({ label, value, first }) {
  return (
    <li
      className="grid grid-cols-[1fr_auto] items-baseline"
      style={{
        borderTop: first ? "none" : "1px solid #EEEDF3",
        padding: "10px 0",
        columnGap: 16,
      }}
    >
      <span style={{ color: "#272636", fontSize: 14 }}>{label}</span>
      <span
        className="tabular-nums"
        style={{
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 600,
          color: "#1B1F4B",
          fontSize: 13,
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </li>
  );
}

function Spec({ label, value, numeric = false }) {
  return (
    <div>
      <div className="wn-label">{label}</div>
      <div
        className={numeric ? "tabular-nums" : ""}
        style={{
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: numeric ? 700 : 600,
          color: "#1B1F4B",
          fontSize: numeric ? 22 : 16,
          marginTop: 4,
          lineHeight: 1.1,
        }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function CostRow({ label, value }) {
  return (
    <div
      className="flex items-baseline justify-between"
      style={{ padding: "8px 0" }}
    >
      <span style={{ color: "#272636", fontSize: 14 }}>{label}</span>
      <span
        className="tabular-nums"
        style={{ fontWeight: 600, color: "#1B1F4B", fontSize: 15 }}
      >
        {value}
      </span>
    </div>
  );
}

/* ─────────── Full-width Action band ─────────── */

function ActionBand({
  tags,
  onConsult,
  onSave,
  isSaved,
  saving,
  visualizerHref,
}) {
  return (
    <div
      data-testid="action-band"
      style={{
        borderTop: "1px solid #D9D8E4",
        borderBottom: "1px solid #D9D8E4",
        padding: "22px 0",
        marginTop: 8,
      }}
    >
      <div className="grid lg:grid-cols-[1fr_auto] items-center gap-6">
        {/* Lifestyle pills */}
        <div className="flex items-center flex-wrap" style={{ gap: 14 }}>
          <span className="wn-label" style={{ color: "#6C6B85" }}>
            Lifestyle & fit
          </span>
          <span aria-hidden style={{ color: "#9AA0C0" }}>·</span>
          <div className="flex flex-wrap gap-2">
            {(tags.length ? tags : ["—"]).map((t) => (
              <span
                key={t}
                style={{
                  padding: "4px 11px",
                  borderRadius: 999,
                  border: "1px solid #D9D8E4",
                  backgroundColor: "transparent",
                  color: "#1B1F4B",
                  fontSize: 12.5,
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                }}
              >
                {(t || "").replace(/_/g, " ")}
              </span>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div className="flex items-center flex-wrap gap-3">
          <button
            className="wn-btn wn-btn-outline"
            onClick={onSave}
            disabled={saving}
            data-testid="save-btn"
            aria-pressed={isSaved}
            style={{ padding: "11px 18px" }}
          >
            <Heart
              size={16}
              fill={isSaved ? "#B14E2F" : "none"}
              color={isSaved ? "#B14E2F" : "currentColor"}
            />
            {isSaved ? "Saved" : "Save"}
          </button>
          <Link
            to={visualizerHref}
            className="wn-btn wn-btn-outline"
            data-testid="visualizer-btn"
            style={{ padding: "11px 18px" }}
          >
            <Layers size={16} />
            Open Visualizer
          </Link>
          <button
            className="wn-btn wn-btn-accent"
            onClick={onConsult}
            data-testid="consult-btn"
            style={{ padding: "11px 22px" }}
          >
            <MessageSquare size={16} />
            Request a Consultation
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Related grid with persona-style filler ─────────── */

function RelatedGrid({ designs, firm, architectId }) {
  // Take up to 3 designs; if fewer, fill the remaining slot with a
  // persona-style "Browse full catalog →" stripe.
  const items = designs.slice(0, 3);
  const showCta = items.length < 3;
  return (
    <div
      className="mt-8 grid md:grid-cols-2 xl:grid-cols-3"
      style={{ gap: 48, alignItems: "stretch" }}
      data-testid="related-grid"
    >
      {items.map((d) => (
        <DesignCard key={d.id} design={d} />
      ))}
      {showCta && (
        <Link
          to={`/architects/${architectId}`}
          data-testid="related-cta"
          className="group"
          style={{
            textDecoration: "none",
            color: "inherit",
            border: "1px solid #D9D8E4",
            borderRadius: 4,
            backgroundColor: "#FBFAFD",
            padding: "26px 28px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            transition: "border-color 220ms ease-out",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.borderColor = "#B14E2F")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.borderColor = "#D9D8E4")
          }
        >
          <div>
            <div className="wn-label" style={{ color: "#8F3C23" }}>
              Architect catalog
            </div>
            <h3
              className="mt-3"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 26,
                color: "#1B1F4B",
                lineHeight: 1.15,
                letterSpacing: "-0.005em",
                margin: 0,
              }}
            >
              Browse {firm}&rsquo;s full catalog.
            </h3>
            <p
              className="mt-4"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              See every design this firm has published on the network — plus
              their practice statement, credentials, and completed projects.
            </p>
          </div>
          <span
            className="mt-6 inline-flex items-center gap-1"
            style={{
              fontWeight: 700,
              color: "#8F3C23",
              fontSize: 13,
            }}
          >
            View profile <ArrowUpRight size={14} />
          </span>
        </Link>
      )}
    </div>
  );
}


// ───────── Architect-owner toolbar ─────────
// Surfaces version / edit actions only when the viewer is the architect
// who owns this design. Lives under the H1 so the page still reads as a
// public marketplace listing for everyone else.
function ArchitectOwnerToolbar({ design, onNewVersion, busy }) {
  const status = design.status;
  const isApproved = status === "Approved";
  const isEditable = status === "Draft" || status === "Needs Revision";
  return (
    <div
      className="flex items-center flex-wrap"
      style={{
        gap: 10,
        marginTop: 18,
        padding: "10px 14px",
        background: "#F4EFE6",
        border: "1px solid #E6DFD0",
        borderRadius: 4,
      }}
      data-testid="architect-owner-toolbar"
    >
      <span
        className="wn-label"
        style={{ color: "#8a651f", marginRight: 4 }}
      >
        You own this design
      </span>
      {design.version && design.version > 1 && (
        <span
          className="inline-flex items-center"
          style={{
            gap: 4,
            padding: "2px 8px",
            borderRadius: 999,
            background: "#E6E7F1",
            color: "#1B1F4B",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          <Layers size={10} strokeWidth={2.4} />
          v{design.version}
        </span>
      )}
      <span style={{ flex: 1 }} />
      {isApproved && (
        <button
          type="button"
          className="wn-btn wn-btn-primary"
          onClick={onNewVersion}
          disabled={busy}
          style={{ fontSize: 12 }}
          data-testid="detail-new-version-btn"
        >
          <Pencil size={12} />
          {busy ? "Opening…" : "Open new version"}
        </button>
      )}
      {isEditable && (
        <Link
          to={`/architect/designs/new?draft=${design.id}`}
          className="wn-btn wn-btn-primary"
          style={{ fontSize: 12 }}
          data-testid="detail-resume-btn"
        >
          <Pencil size={12} /> Resume editing
        </Link>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// PriorBuildsBlock — close-out actuals from past completed builds of
// this design. Each card carries the address, completion date, owner
// label, actual cost + variance vs estimate, timeline, and the licensed
// contractors who pulled permits (with their license + permit numbers)
// so a developer / CDC / owner can call contractors who've already
// built this exact plan.
// ───────────────────────────────────────────────────────────────────
function PriorBuildsBlock({ builds }) {
  // Compute returning-contractor flag: any contractor (keyed by
  // license_no, falling back to firm name) that appears on more than
  // one build of this design gets a "Returning contractor" badge. This
  // is a meaningful safety signal for a prospective owner — a licensed
  // firm that has built this exact plan multiple times has already
  // priced and detailed it before.
  const returningKeys = (() => {
    const counts = {};
    builds.forEach((b) => {
      const seenInThisBuild = new Set();
      (b.contractors || []).forEach((c) => {
        const key = (c.license_no || c.name || "").trim();
        if (!key || seenInThisBuild.has(key)) return;
        seenInThisBuild.add(key);
        counts[key] = (counts[key] || 0) + 1;
      });
    });
    return new Set(
      Object.entries(counts)
        .filter(([, n]) => n > 1)
        .map(([k]) => k)
    );
  })();

  const returningCount = returningKeys.size;

  return (
    <div
      className="mt-6 space-y-5"
      data-testid="prior-builds-block"
    >
      {returningCount > 0 && (
        <div
          data-testid="returning-contractor-summary"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            fontSize: 13,
            color: "#4F6149",
            background: "#F4F2EC",
            border: "1px solid #DDD9CE",
            borderLeft: "3px solid #4F6149",
            borderRadius: 4,
            padding: "10px 14px",
            lineHeight: 1.55,
          }}
        >
          <span style={{ fontWeight: 700, fontStyle: "normal" }}>
            {returningCount} returning{" "}
            {returningCount === 1 ? "contractor" : "contractors"}
          </span>{" "}
          have pulled permits on this plan more than once — they already
          know its details, pricing, and inspection sequence.
        </div>
      )}
      {builds.map((b) => {
        const completedLabel = (() => {
          try {
            return new Date(b.completed_at).toLocaleDateString("en-US", {
              month: "long", year: "numeric",
            });
          } catch { return b.completed_at || "—"; }
        })();
        const variance = Number(b.variance_vs_estimate_pct || 0);
        const varianceColor =
          variance > 0 ? "#A23B2E" : variance < 0 ? "#4F6149" : "#6C6B85";
        return (
          <div
            key={b.id}
            data-testid={`prior-build-${b.id}`}
            style={{
              border: "1px solid #D9D8E4",
              borderRadius: 6,
              background: "#FBFAFD",
            }}
          >
            {/* Header strip */}
            <div
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid #ECEBF1",
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 14,
                alignItems: "baseline",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 500, fontSize: 17, color: "#1B1F4B",
                    lineHeight: 1.3,
                  }}
                >
                  {b.address}
                </div>
                <div
                  style={{
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic", color: "#6C6B85",
                    fontSize: 13, marginTop: 2,
                  }}
                >
                  {b.neighborhood} · {b.owner_label} · completed{" "}
                  {completedLabel}
                </div>
              </div>
              <div
                className="tabular-nums"
                style={{ textAlign: "right" }}
              >
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontSize: 22, fontWeight: 500, color: "#1B1F4B",
                    lineHeight: 1, letterSpacing: "-0.01em",
                  }}
                >
                  {formatPriceFull(b.actual_total_cost)}
                </div>
                <div
                  style={{
                    fontSize: 11.5, color: "#6C6B85", marginTop: 4,
                  }}
                >
                  actual all-in ·{" "}
                  <span style={{ color: varianceColor, fontWeight: 700 }}>
                    {variance > 0 ? "+" : ""}{variance.toFixed(1)}%
                  </span>{" "}
                  vs estimate
                </div>
              </div>
            </div>

            {/* Timeline + contractor permits */}
            <div style={{ padding: "12px 18px 16px" }}>
              <div
                className="wn-label"
                style={{ color: "#8F3C23" }}
              >
                Build duration · {b.actual_timeline_months} months
              </div>
              <div
                className="wn-label mt-3"
                style={{ color: "#8F3C23" }}
              >
                Permits pulled · licensed contractors
              </div>
              <ul
                style={{
                  margin: "6px 0 0", padding: 0, listStyle: "none",
                }}
              >
                {(b.contractors || []).map((c, i) => {
                  const key = (c.license_no || c.name || "").trim();
                  const isReturning = key && returningKeys.has(key);
                  return (
                  <li
                    key={i}
                    data-testid={`prior-build-contractor-${b.id}-${i}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.4fr 1fr auto",
                      gap: 12,
                      alignItems: "baseline",
                      padding: "8px 0",
                      borderTop: "1px solid #ECEBF1",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontWeight: 700, color: "#1B1F4B", fontSize: 13.5,
                        display: "flex", flexWrap: "wrap",
                        alignItems: "center", gap: 8,
                      }}>
                        <span>{c.name}</span>
                        {isReturning && (
                          <span
                            data-testid={`returning-contractor-badge-${b.id}-${i}`}
                            style={{
                              fontFamily: '"Inter", system-ui, sans-serif',
                              fontWeight: 600,
                              fontSize: 9.5,
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                              color: "#3A4A35",
                              background: "#E8EAD9",
                              border: "1px solid #B8BFA0",
                              borderRadius: 3,
                              padding: "2px 7px",
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                            }}
                            title="This firm has pulled permits on more than one build of this exact plan."
                          >
                            Returning contractor
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontFamily: '"Newsreader", Georgia, serif',
                        fontStyle: "italic", color: "#6C6B85",
                        fontSize: 11.5, marginTop: 2,
                      }}>
                        {c.role} · License {c.license_no}
                      </div>
                    </div>
                    <div
                      className="tabular-nums"
                      style={{
                        fontSize: 11.5, color: "#6C6B85",
                      }}
                    >
                      Permit {c.permit_no}
                      <br />
                      <span style={{ color: "#9AA0C0" }}>
                        pulled {c.permit_date}
                      </span>
                    </div>
                    <div
                      className="tabular-nums"
                      style={{
                        textAlign: "right",
                        fontFamily: '"Fraunces", Georgia, serif',
                        fontSize: 15, color: "#1B1F4B", fontWeight: 500,
                      }}
                    >
                      {formatPriceFull(c.work_cost)}
                    </div>
                  </li>
                  );
                })}
              </ul>
              <p
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic", fontSize: 11.5, color: "#9AA0C0",
                  margin: "10px 0 0", lineHeight: 1.5,
                }}
              >
                Pulled from the City of Indianapolis DBNS permits feed.
                Reach out to these firms — they&apos;ve already built
                this plan and know its details inside-out.
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

